import React, { useRef } from 'react';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Badge,
  Box,
  Button,
  Checkbox,
  Divider,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { useQuery } from '@apollo/client';
import { Form, Formik } from 'formik';
import moment from 'moment';
import * as Yup from 'yup';

import Input from '../../../components/Input';

import {
  AmazonHubCounterPayloadQuery,
  AmazonFeedsQuery,
  AmazonHubCounterPayloadStandardHours,
  GET_AMAZON_FEED_DETAILS,
  GET_AMAZON_FEEDS,
  GET_AMAZON_HUB_COUNTER_PAYLOAD,
  HubCounter,
  HubCounterStateQuery,
} from '../query';
import { HubCounterDiagnostics } from '../Tabs/hubCounterState';

const feedStatusColor: Record<string, string> = {
  PENDING: 'yellow',
  SUBMITTED: 'blue',
  IN_PROGRESS: 'yellow',
  COMPLETED: 'green',
  COMPLETED_WITH_WARNINGS: 'orange',
  CANCELLED: 'gray',
  FAILED: 'red',
};

const InfoRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Flex alignItems="flex-start" gap="2" mb="1">
    <Text fontSize="sm" color="gray.500" minW="180px" flexShrink={0}>
      {label}
    </Text>
    <Box fontSize="sm" fontWeight="medium" flex="1">
      {value ?? (
        <Text as="span" color="gray.400">
          —
        </Text>
      )}
    </Box>
  </Flex>
);

const PAYLOAD_DAY_LABELS: Record<string, string> = {
  MONDAY: 'Montag',
  TUESDAY: 'Dienstag',
  WEDNESDAY: 'Mittwoch',
  THURSDAY: 'Donnerstag',
  FRIDAY: 'Freitag',
  SATURDAY: 'Samstag',
  SUNDAY: 'Sonntag',
};

const renderStandardHours = (standardHours: AmazonHubCounterPayloadStandardHours[]) => {
  if (!standardHours.length) {
    return undefined;
  }

  return (
    <Box>
      {standardHours.map((hours) => (
        <Box key={`${hours.day}-${hours.openingTime}-${hours.closingTime}`} mb="2">
          <Text fontSize="sm" fontWeight="medium">
            {PAYLOAD_DAY_LABELS[hours.day] || hours.day}: {hours.openingTime} - {hours.closingTime}
          </Text>
          {hours.midDayClosures?.map((closure, index) => (
            <Text key={`${hours.day}-${closure.startTime}-${closure.endTime}-${index}`} fontSize="sm" color="gray.600">
              Pause {index + 1}: {closure.startTime} - {closure.endTime}
            </Text>
          ))}
        </Box>
      ))}
    </Box>
  );
};

const FeedHistory = ({ hubCounterId }: { hubCounterId: string }) => {
  const { data, loading, error, refetch } = useQuery<AmazonFeedsQuery>(GET_AMAZON_FEEDS, {
    variables: { hubCounterId },
    fetchPolicy: 'cache-and-network',
  });

  if (loading) {
    return (
      <Flex justifyContent="center" py="4">
        <Spinner size="sm" />
      </Flex>
    );
  }

  if (error) {
    return (
      <Flex alignItems="center" gap="2" py="2">
        <Text fontSize="sm" color="red.500">
          Feed-Verlauf konnte nicht geladen werden.
        </Text>
        <Button size="xs" variant="outline" colorScheme="red" onClick={() => refetch()}>
          Erneut versuchen
        </Button>
      </Flex>
    );
  }

  const feeds = data?.amazonFeeds ?? [];

  if (feeds.length === 0) {
    return (
      <Text fontSize="sm" color="gray.500" py="2">
        Noch keine Feeds gesendet.
      </Text>
    );
  }

  return (
    <TableContainer>
      <Table size="sm" variant="simple">
        <Thead>
          <Tr>
            <Th>Feed-ID</Th>
            <Th>Typ</Th>
            <Th>Status</Th>
            <Th>Aktion</Th>
            <Th isNumeric>Verarbeitet</Th>
            <Th isNumeric>Erfolgreich</Th>
            <Th isNumeric>Fehler</Th>
            <Th>Erstellt</Th>
          </Tr>
        </Thead>
        <Tbody>
          {feeds.map((feed) => (
            <Tr key={feed.id}>
              <Td fontFamily="mono" fontSize="xs">
                {feed.feedId ?? '—'}
              </Td>
              <Td fontSize="xs">{feed.feedType ?? '—'}</Td>
              <Td>
                <Badge colorScheme={feedStatusColor[feed.status ?? ''] ?? 'gray'} fontSize="xs">
                  {feed.status ?? '—'}
                </Badge>
              </Td>
              <Td fontSize="xs">{feed.operation ?? '—'}</Td>
              <Td isNumeric>{feed.noProcessed ?? '—'}</Td>
              <Td isNumeric>{feed.noSuccess ?? '—'}</Td>
              <Td isNumeric>
                <Text as="span" color={(feed.noFailed ?? 0) > 0 ? 'red.500' : 'inherit'}>
                  {feed.noFailed ?? '—'}
                </Text>
              </Td>
              <Td fontSize="xs">{feed.submittedAt ? moment(feed.submittedAt).format('DD.MM.YY HH:mm') : '—'}</Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </TableContainer>
  );
};

interface HubCounterDeveloperUtilitiesProps {
  isOpen: boolean;
  onClose: () => void;
  dropPointId: string;
  hubCounter: HubCounter;
  diagnostics: HubCounterDiagnostics;
  stateSnapshot?: HubCounterStateQuery['hubCounter'];
  lifecycleCtas?: React.ReactNode;
  onRequestAction: (action: HubCounterConfirmAction) => void;
  updateLoading: boolean;
  createLoading: boolean;
  deactivateLoading: boolean;
  refreshLoading: boolean;
}

export type HubCounterConfirmAction = 'create' | 'update' | 'deactivate' | 'refresh' | 'pending' | 'waitlist';

export type HubCounterEndorActionPayload = {
  immediate: boolean;
  comment: string;
};

const ActionConfirmSchema = Yup.object().shape({
  comment: Yup.string().required('Du musst einen Kommentar/Begründung eingeben'),
});

const ACTION_CONFIRM_COPY: Record<
  HubCounterConfirmAction,
  { title: string; body: string; confirmLabel: string; colorScheme: string; showImmediate: boolean }
> = {
  create: {
    title: 'CREATE-Feed senden?',
    body: 'Der CREATE-Feed wird an Amazon übermittelt. Ohne Checkbox wartet er auf das 2-Stunden-Fenster.',
    confirmLabel: 'Senden',
    colorScheme: 'green',
    showImmediate: true,
  },
  update: {
    title: 'Bei Amazon aktualisieren?',
    body: 'Ein UPDATE-Feed wird an Amazon übermittelt. Ohne Checkbox wartet er auf das 2-Stunden-Fenster.',
    confirmLabel: 'Aktualisieren',
    colorScheme: 'blue',
    showImmediate: true,
  },
  deactivate: {
    title: 'Hub Counter bei Amazon deaktivieren?',
    body: 'isActive wird lokal auf false gesetzt und ein UPDATE-Feed an Amazon übermittelt. Ohne Checkbox wartet der Feed auf das 2-Stunden-Fenster.',
    confirmLabel: 'Deaktivieren',
    colorScheme: 'red',
    showImmediate: true,
  },
  refresh: {
    title: 'Session aktualisieren?',
    body: 'Die gespeicherte Amazon Hub Session wird neu geladen und geprüft.',
    confirmLabel: 'Aktualisieren',
    colorScheme: 'orange',
    showImmediate: false,
  },
  pending: {
    title: 'Auf PENDING setzen?',
    body: 'Der Hub Counter wird intern auf PENDING gesetzt.',
    confirmLabel: 'Auf PENDING setzen',
    colorScheme: 'brand',
    showImmediate: false,
  },
  waitlist: {
    title: 'Auf WAITLIST setzen?',
    body: 'Der Hub Counter wird intern auf WAITLIST gesetzt.',
    confirmLabel: 'Auf WAITLIST setzen',
    colorScheme: 'brand',
    showImmediate: false,
  },
};

export const HubCounterActionConfirmDialog = ({
  action,
  onClose,
  onConfirm,
  isLoading,
}: {
  action: HubCounterConfirmAction | null;
  onClose: () => void;
  onConfirm: (payload: HubCounterEndorActionPayload) => Promise<void>;
  isLoading: boolean;
}) => {
  const confirmCancelRef = useRef<HTMLButtonElement>(null);
  const confirmCopy = action ? ACTION_CONFIRM_COPY[action] : null;

  return (
    <AlertDialog isOpen={Boolean(action)} leastDestructiveRef={confirmCancelRef} onClose={onClose}>
      <AlertDialogOverlay />
      <AlertDialogContent>
        {confirmCopy ? (
          <Formik
            key={action}
            initialValues={{ comment: '', immediate: false }}
            validationSchema={ActionConfirmSchema}
            onSubmit={onConfirm}
          >
            {({ values, handleChange }) => (
              <Form>
                <AlertDialogHeader fontSize="lg" fontWeight="bold">
                  {confirmCopy.title}
                </AlertDialogHeader>
                <AlertDialogBody>
                  <Text mb="3">{confirmCopy.body}</Text>
                  <Input name="comment" label="Kommentar/Begründung" />
                  {confirmCopy.showImmediate ? (
                    <>
                      <Checkbox name="immediate" isChecked={values.immediate} onChange={handleChange} mb="2">
                        Sofort an Amazon senden (Queue umgehen)
                      </Checkbox>
                      <Text fontSize="xs" color="gray.500">
                        Sendet den Feed jetzt statt auf das 2-Stunden-Fenster zu warten. Zählt gegen das Tagesbudget.
                      </Text>
                    </>
                  ) : null}
                </AlertDialogBody>
                <AlertDialogFooter>
                  <Button ref={confirmCancelRef} onClick={onClose}>
                    Abbrechen
                  </Button>
                  <Button colorScheme={confirmCopy.colorScheme} type="submit" ml={3} isLoading={isLoading}>
                    {confirmCopy.confirmLabel}
                  </Button>
                </AlertDialogFooter>
              </Form>
            )}
          </Formik>
        ) : null}
      </AlertDialogContent>
    </AlertDialog>
  );
};

const HubCounterDeveloperUtilities = ({
  isOpen,
  onClose,
  dropPointId,
  hubCounter,
  diagnostics,
  stateSnapshot,
  lifecycleCtas,
  onRequestAction,
  updateLoading,
  createLoading,
  deactivateLoading,
  refreshLoading,
}: HubCounterDeveloperUtilitiesProps) => {
  const { data: feedDetails } = useQuery(GET_AMAZON_FEED_DETAILS, {
    variables: { feedId: hubCounter.lastFeedId },
    skip: !hubCounter.lastFeedId || !isOpen,
    fetchPolicy: 'cache-and-network',
  });
  const {
    data: payloadData,
    loading: payloadLoading,
    error: payloadError,
  } = useQuery<AmazonHubCounterPayloadQuery>(GET_AMAZON_HUB_COUNTER_PAYLOAD, {
    variables: { dropPointId },
    skip: !hubCounter.hubCounterID || !isOpen,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });
  const payload = payloadData?.amazonHubCounterPayload;
  const latestDocumentErrors = (feedDetails?.amazonFeed?.documents || [])
    .flatMap((document: any) => document?.errors || [])
    .filter(Boolean);
  const showVisualWaitlistHint = diagnostics.displayState.value === 'WAITLIST' && !diagnostics.canConfirmAmazon;
  const canUpdate =
    hubCounter.status === 'CONFIRMED' ||
    hubCounter.status === 'ACTIVE' ||
    hubCounter.status === 'INACTIVE' ||
    hubCounter.status === 'RETRY';
  const hasHubCounterId = Boolean(hubCounter.hubCounterID);

  return (
    <Drawer isOpen={isOpen} onClose={onClose} size="xl" autoFocus={false}>
      <DrawerOverlay />
      <DrawerContent>
        <DrawerHeader>Developer Utilities</DrawerHeader>
        <DrawerCloseButton />
        <DrawerBody pb="6">
          <Flex alignItems="center" gap="2" mb="4" flexWrap="wrap">
            <Badge colorScheme={diagnostics.lifecycle.colorScheme} fontSize="sm" px="2" py="1">
              {diagnostics.lifecycle.label}: {diagnostics.lifecycle.value}
            </Badge>
            <Badge colorScheme={diagnostics.displayState.colorScheme} fontSize="sm" px="2" py="1">
              {diagnostics.displayState.label}: {diagnostics.displayState.value}
            </Badge>
            <Badge colorScheme={diagnostics.feed.colorScheme} fontSize="sm" px="2" py="1">
              {diagnostics.feed.label}: {diagnostics.feed.value}
            </Badge>
            <Badge colorScheme={diagnostics.session.colorScheme} fontSize="sm" px="2" py="1">
              {diagnostics.session.label}: {diagnostics.session.value}
            </Badge>
            {hubCounter.isStaging ? (
              <Badge colorScheme="purple" fontSize="sm" px="2" py="1">
                Staging
              </Badge>
            ) : null}
          </Flex>

          {showVisualWaitlistHint ? (
            <Box mb="4" p="3" bg="orange.50" borderRadius="md">
              <Text fontSize="xs" fontWeight="semibold" color="orange.700" mb="1">
                Sichtbar weiter auf WAITLIST
              </Text>
              <Text fontSize="sm" color="orange.800">
                Der Hub Counter ist intern bereits {hubCounter.status}, bleibt aber im App-Status WAITLIST, bis der CREATE-Feed
                abgeschlossen ist.
              </Text>
            </Box>
          ) : null}

          <Text fontWeight="semibold" fontSize="sm" mb="3">
            Lifecycle-Aktionen
          </Text>
          <Flex flexWrap="wrap" gap="2" mb="4">
            {lifecycleCtas || (
              <Text fontSize="sm" color="gray.500">
                Keine Lifecycle-Aktion für diesen Status.
              </Text>
            )}
          </Flex>

          <Text fontWeight="semibold" fontSize="sm" mb="3">
            Technische Aktionen
          </Text>
          <Flex flexWrap="wrap" gap="2" mb="4">
            {hasHubCounterId ? (
              <Button size="sm" colorScheme="green" variant="outline" onClick={() => onRequestAction('create')} isLoading={createLoading}>
                CREATE-Feed senden
              </Button>
            ) : null}
            {canUpdate ? (
              <Button size="sm" colorScheme="blue" onClick={() => onRequestAction('update')} isLoading={updateLoading}>
                Bei Amazon aktualisieren
              </Button>
            ) : null}
            {hasHubCounterId ? (
              <Button
                size="sm"
                colorScheme="red"
                variant="outline"
                onClick={() => onRequestAction('deactivate')}
                isLoading={deactivateLoading}
              >
                Bei Amazon deaktivieren
              </Button>
            ) : null}
            {hasHubCounterId ? (
              <Button
                size="sm"
                colorScheme="orange"
                variant="outline"
                onClick={() => onRequestAction('refresh')}
                isLoading={refreshLoading}
              >
                Session aktualisieren
              </Button>
            ) : null}
          </Flex>

          <Divider my="4" />

          <Text fontWeight="semibold" fontSize="sm" mb="3">
            Lifecycle
          </Text>
          <InfoRow
            label="Interner Lifecycle"
            value={<Badge colorScheme={diagnostics.lifecycle.colorScheme}>{diagnostics.lifecycle.value}</Badge>}
          />
          <InfoRow
            label="Effektiver App-Status"
            value={<Badge colorScheme={diagnostics.displayState.colorScheme}>{diagnostics.displayState.value}</Badge>}
          />
          <InfoRow label="CREATE Feed abgeschlossen" value={stateSnapshot?.createFeedCompleted ? 'Ja' : 'Nein'} />
          <InfoRow label="Hub Counter ID" value={hubCounter.hubCounterID} />
          <InfoRow label="Amazon Access Point ID" value={hubCounter.amazonAccessPointId} />
          <InfoRow label="Name" value={hubCounter.name} />
          <InfoRow label="Erstellt" value={hubCounter.createdAt ? moment(hubCounter.createdAt).format('DD.MM.YYYY HH:mm') : undefined} />
          <InfoRow
            label="Zuletzt aktualisiert"
            value={hubCounter.updatedAt ? moment(hubCounter.updatedAt).format('DD.MM.YYYY HH:mm') : undefined}
          />

          <Divider my="4" />

          <Text fontWeight="semibold" fontSize="sm" mb="3">
            Feed
          </Text>
          <InfoRow
            label="Queue / letzter Feed"
            value={<Badge colorScheme={diagnostics.feed.colorScheme}>{diagnostics.feed.value}</Badge>}
          />
          <InfoRow label="CREATE Feed abgeschlossen" value={stateSnapshot?.createFeedCompleted ? 'Ja' : 'Nein'} />
          <InfoRow
            label="Letzte Feed-ID"
            value={
              hubCounter.lastFeedId ? (
                <Text fontFamily="mono" fontSize="xs">
                  {hubCounter.lastFeedId}
                </Text>
              ) : undefined
            }
          />
          <InfoRow
            label="Letzter Feed-Status"
            value={
              hubCounter.lastFeedStatus ? (
                <Badge colorScheme={feedStatusColor[hubCounter.lastFeedStatus] ?? 'gray'}>{hubCounter.lastFeedStatus}</Badge>
              ) : undefined
            }
          />

          {latestDocumentErrors.length > 0 ? (
            <Box mt="3" p="3" bg="red.50" borderRadius="md">
              <Text fontSize="xs" fontWeight="semibold" color="red.600" mb="2">
                Fehler im letzten Output-Dokument
              </Text>
              <Text as="pre" whiteSpace="pre-wrap" fontSize="xs" color="red.700">
                {JSON.stringify(latestDocumentErrors, null, 2)}
              </Text>
            </Box>
          ) : null}

          <Divider my="4" />

          <Text fontWeight="semibold" fontSize="sm" mb="3">
            Session
          </Text>
          <InfoRow
            label="Session-Status"
            value={<Badge colorScheme={diagnostics.session.colorScheme}>{diagnostics.session.value}</Badge>}
          />
          <InfoRow label="Session verifiziert" value={stateSnapshot?.sessionVerified ? 'Ja' : 'Nein'} />
          <InfoRow
            label="Letzte Autorisierung"
            value={stateSnapshot?.lastAuthorizeAt ? moment(stateSnapshot.lastAuthorizeAt).format('DD.MM.YYYY HH:mm') : undefined}
          />
          <InfoRow
            label="Session läuft ab"
            value={stateSnapshot?.sessionExpiresAt ? moment(stateSnapshot.sessionExpiresAt).format('DD.MM.YYYY HH:mm') : undefined}
          />

          {payloadError && !payloadLoading ? (
            <Box mt="3" p="3" bg="gray.50" borderRadius="md">
              <Text fontSize="xs" fontWeight="semibold" color="gray.600" mb="1">
                Technische Payload aktuell nicht verfügbar
              </Text>
              <Text fontSize="sm" color="gray.700">
                {payloadError.message}
              </Text>
            </Box>
          ) : null}

          {payload ? (
            <>
              <Divider my="4" />
              <Text fontWeight="semibold" fontSize="sm" mb="3">
                Technische Payload
              </Text>
              <InfoRow label="Payload Access Point ID" value={payload.accessPointId} />
              <InfoRow label="Aktiv" value={payload.isActive ? 'Ja' : 'Nein'} />
              <InfoRow label="Restricted Access" value={payload.isRestrictedAccess ? 'Ja' : 'Nein'} />
              <InfoRow label="Zeitzone" value={payload.timeZone} />
              <InfoRow label="Address Field 1" value={payload.address.addressFieldOne} />
              <InfoRow label="City" value={payload.address.city} />
              <InfoRow label="Region" value={payload.address.region} />
              <InfoRow label="District" value={payload.address.district} />
              <InfoRow label="Postal Code" value={payload.address.postalCode} />
              <InfoRow label="Country" value={payload.address.countryCode} />
              <InfoRow label="Latitude" value={payload.address.latitude} />
              <InfoRow label="Longitude" value={payload.address.longitude} />
              <InfoRow label="Standard Hours" value={renderStandardHours(payload.standardHours)} />
            </>
          ) : null}

          {hubCounter.lastDeactivationTimestamp || hubCounter.deactivatedUntil ? (
            <>
              <Divider my="4" />
              <Text fontWeight="semibold" fontSize="sm" mb="3">
                Historie
              </Text>
              {hubCounter.lastDeactivationTimestamp ? (
                <InfoRow label="Letzte Deaktivierung" value={moment(hubCounter.lastDeactivationTimestamp).format('DD.MM.YYYY HH:mm')} />
              ) : null}
              {hubCounter.deactivatedUntil ? (
                <InfoRow label="Deaktiviert bis" value={moment(hubCounter.deactivatedUntil).format('DD.MM.YYYY HH:mm')} />
              ) : null}
            </>
          ) : null}

          <Divider my="4" />

          <Text fontWeight="semibold" fontSize="sm" mb="3">
            Feed-Verlauf
          </Text>
          <FeedHistory hubCounterId={hubCounter.hubCounterID || hubCounter.id} />
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

export default HubCounterDeveloperUtilities;
